import {
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@healthinal/ui';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';

export interface EditableAnamnesisItem<TType extends string> {
  id?: string | null;
  started?: string;
  type: TType;
}

interface EditAnamnesisItemsDialogProps<TType extends string> {
  open: boolean;
  title: string;
  typeLabel: string;
  emptyText: string;
  items: EditableAnamnesisItem<TType>[];
  typeOptions: { label: string; value: TType }[];
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (items: EditableAnamnesisItem<TType>[]) => void;
}

export function EditAnamnesisItemsDialog<TType extends string>({
  open,
  title,
  typeLabel,
  emptyText,
  items,
  typeOptions,
  isLoading,
  onClose,
  onSubmit,
}: EditAnamnesisItemsDialogProps<TType>) {
  const [editableItems, setEditableItems] = useState(items);
  const wasOpenRef = useRef(open);
  const shouldScrollItems = editableItems.length > 10;
  const hasInvalidItems = editableItems.some((item) => !item.type);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setEditableItems(items);
    }

    wasOpenRef.current = open;
  }, [items, open]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (hasInvalidItems) {
      return;
    }

    onSubmit(editableItems.map((item) => ({ ...item, started: item.started || undefined })));
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: 760 }}>
        <ModalClose />
        <Typography level={'h4'}>{title}</Typography>
        <Divider />
        <form onSubmit={handleSubmit}>
          <Stack gap={2}>
            {editableItems.length ? (
              <Sheet
                sx={
                  shouldScrollItems
                    ? { maxHeight: 'calc(var(--TableCell-height, 40px) * 11)', overflow: 'auto' }
                    : undefined
                }>
                <Table stickyHeader={shouldScrollItems}>
                  <thead>
                    <tr>
                      <th style={{ width: 180 }}>Seit</th>
                      <th>{typeLabel}</th>
                      <th style={{ width: 48 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {editableItems.map((item, index) => (
                      <tr key={item.id ?? index}>
                        <td>
                          <FormControl>
                            <GermanDateInput
                              dateInputMode="freeText"
                              value={item.started ?? ''}
                              onChange={(value) =>
                                setEditableItems((currentItems) =>
                                  currentItems.map((currentItem, currentIndex) =>
                                    currentIndex === index ? { ...currentItem, started: value } : currentItem,
                                  ),
                                )
                              }
                            />
                          </FormControl>
                        </td>
                        <td>
                          <FormControl>
                            <FormLabel sx={{ display: 'none' }}>{typeLabel}</FormLabel>
                            <Select
                              value={item.type}
                              onChange={(_, value) =>
                                value &&
                                setEditableItems((currentItems) =>
                                  currentItems.map((currentItem, currentIndex) =>
                                    currentIndex === index ? { ...currentItem, type: value } : currentItem,
                                  ),
                                )
                              }>
                              {typeOptions.map((option) => (
                                <Option value={option.value} key={option.value}>
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          </FormControl>
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          <IconButton
                            color={'danger'}
                            onClick={() =>
                              setEditableItems((currentItems) =>
                                currentItems.filter((_, currentIndex) => currentIndex !== index),
                              )
                            }
                            title={'Eintrag entfernen'}
                            type={'button'}>
                            <Icons.Delete />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            ) : (
              <Stack alignItems={'center'} gap={1} p={3}>
                <Icons.Description />
                <Typography level="body-sm">{emptyText}</Typography>
              </Stack>
            )}
            <FormActionButtons onAbortClick={handleClose} disableSubmit={hasInvalidItems} isLoading={isLoading} />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
